import { useState, type ReactNode } from 'react'
import { Table } from '@mantine/core'
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react'
import { ChangeText } from '../components/ChangeText'
import { InfoIcon } from '../components/InfoIcon'
import { SectionCard } from '../components/SectionCard'
import { StatusBadge } from '../components/StatusBadge'
import { formatCount, formatDays } from '../format'
import type { CohortTarget, usePopulationMeasures } from '../hooks'
import { sortMeasures, type SortKey } from '../utils'

function SortHeader({
  label,
  column,
  sort,
  onSort,
  numeric = false,
  children,
}: {
  label: string
  column: SortKey
  sort: { key: SortKey; descending: boolean }
  onSort: (key: SortKey) => void
  /** A number column: header and cells align right. */
  numeric?: boolean
  children?: ReactNode
}) {
  const active = sort.key === column
  const Arrow = sort.descending ? IconArrowDown : IconArrowUp
  return (
    <Table.Th
      aria-sort={active ? (sort.descending ? 'descending' : 'ascending') : 'none'}
      className="whitespace-nowrap"
      ta={numeric ? 'right' : undefined}
    >
      <div className={`flex items-center gap-0.5 ${numeric ? 'justify-end' : ''}`}>
        <button
          type="button"
          onClick={() => onSort(column)}
          className="flex cursor-pointer items-center gap-1 text-[11px] font-semibold tracking-wider text-slate-500 uppercase"
        >
          {label}
          {active && <Arrow size={12} />}
        </button>
        {children}
      </div>
    </Table.Th>
  )
}

/** Every active measure, ranked by default and sortable; a row opens that measure's drawer. */
export function CareGapTable({
  measures,
  onOpenCohort,
}: {
  measures: ReturnType<typeof usePopulationMeasures>
  onOpenCohort: (target: CohortTarget) => void
}) {
  const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({
    key: 'rank',
    descending: false,
  })
  const onSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, descending: !prev.descending }
        : { key, descending: key !== 'rank' && key !== 'label' },
    )
  const list = measures.data?.measures ?? []
  const rows = sortMeasures(list, sort.key, sort.descending)
  const definitions = measures.data?.definitions
  const header = { sort, onSort }

  return (
    <SectionCard
      title="Care-gap performance"
      definition={definitions?.ranking}
      query={measures}
      isEmpty={list.length === 0}
      emptyText="No active measures."
    >
      <Table.ScrollContainer minWidth={900}>
        <Table highlightOnHover verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <SortHeader label="#" column="rank" {...header} />
              <SortHeader label="Measure" column="label" {...header} />
              <SortHeader numeric label="Open patients" column="openPatients" {...header} />
              <SortHeader numeric label="High / Medium" column="highPatients" {...header} />
              <SortHeader numeric label="Opened in period" column="openedInPeriod" {...header} />
              <SortHeader numeric label="Median time to resolve" column="days" {...header}>
                {definitions && (
                  <InfoIcon label="Median time to resolve" definition={definitions.closureTime} />
                )}
              </SortHeader>
              <SortHeader numeric label="vs prior" column="change" {...header} />
              <SortHeader label="Status" column="status" {...header}>
                {definitions && <InfoIcon label="Status" definition={definitions.status} />}
              </SortHeader>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((m) => (
              <Table.Tr
                key={m.slug}
                className="cursor-pointer"
                onClick={() => onOpenCohort({ type: 'measure', slug: m.slug })}
              >
                <Table.Td className="text-gray-500 tabular-nums">{m.rank}</Table.Td>
                <Table.Td>
                  {/* The row is clickable; the name is its keyboard-reachable equivalent. */}
                  <button
                    type="button"
                    className="cursor-pointer text-left font-medium"
                    onClick={(event) => {
                      event.stopPropagation()
                      onOpenCohort({ type: 'measure', slug: m.slug })
                    }}
                  >
                    {m.label}
                  </button>
                  <div className="text-xs text-gray-500">{m.group.label}</div>
                </Table.Td>
                <Table.Td ta="right" className="tabular-nums">
                  {formatCount(m.openPatients)}
                </Table.Td>
                <Table.Td ta="right" className="tabular-nums">
                  {formatCount(m.highPatients)} / {formatCount(m.mediumPatients)}
                </Table.Td>
                <Table.Td ta="right" className="tabular-nums">
                  {formatCount(m.openedInPeriod)}
                </Table.Td>
                <Table.Td ta="right" className="tabular-nums">
                  {formatDays(m.closureTime.current.medianDays)}
                </Table.Td>
                <Table.Td ta="right">
                  <ChangeText value={m.closureTime.changeDays} unit="days" better="down" label="" />
                </Table.Td>
                <Table.Td>
                  <StatusBadge status={m.status} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </SectionCard>
  )
}
